import { type ComponentProps, useCallback } from 'react';

import { useSelector } from '@xstate/react';
import { type SnapshotFrom } from 'xstate';

import { type JoinOrSignInForm } from '../../JoinOrSignInForm';
import { useJoinOrSignInFrameMessages } from '../hooks';
import { type joinOrSignInStateMachine } from './join-or-sign-in-machine';
import { useJoinOrSignInService } from './useJoinOrSignInService';

// ─────────────────────────────────────────────────────────────────────────────

export const useJoinOrSignIn = (params: UseJoinOrSignInParams) => {
  const { onFrameHeightChange } = params;

  const joinOrSignInService = useJoinOrSignInService();

  const handleIncomingMessage = useJoinOrSignInFrameMessages({
    joinOrSignInService,
    onFrameHeightChange,
  });

  const states = useSelector(joinOrSignInService, getStates);

  const context = useSelector(joinOrSignInService, getContext);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const submitAccountData = useCallback<ComponentProps<typeof JoinOrSignInForm>['onSubmit']>(
    (payload) => {
      joinOrSignInService.send({
        type: 'SUBMIT_ACCOUNT_DATA',
        input: {
          firstName: payload.firstName,
          lastName: payload.lastName,
          phoneNumber: payload.phoneNumber,
          birthday: payload.birthday,
          zipCode: payload.zipCode,
        },
      });
    },
    [joinOrSignInService],
  );

  const restartAuthenticationFlow = useCallback(() => {
    joinOrSignInService.send({ type: 'RESTART' });
  }, [joinOrSignInService]);

  // ─────────────────────────────────────────────────────────────────────

  return {
    states,
    context,
    submitAccountData,
    handleIncomingMessage,
    restartAuthenticationFlow,
  };
};

// ─── Selectors ───────────────────────────────────────────────────────────────

function getStates(snapshot: Snapshot) {
  return {
    isLoadingEmailAddress: snapshot.matches('waiting'),
    isEmailAddressView:
      snapshot.matches('entering-email-address') ||
      snapshot.matches('waiting') ||
      snapshot.matches('verification-code-loading'),
    isLoadingVerificationCode:
      snapshot.matches('verification-code-loading') || snapshot.matches('verification'),
    isVerificationCodeView:
      snapshot.matches('entering-verification-code') || snapshot.matches('verification'),
    isFillAccountDataView:
      snapshot.matches('filling-account-data') ||
      snapshot.matches('refreshing-access-tokens') ||
      snapshot.matches('creating-account'),
    isSigningIn: snapshot.matches('signing-in'),
    isCreatingAccount:
      snapshot.matches('refreshing-access-tokens') || snapshot.matches('creating-account'),
    isAuthenticationFailed: snapshot.matches('authentication-failed'),
    shouldRedirect: snapshot.matches('redirect'),
  };
}

function getContext(snapshot: Snapshot) {
  return snapshot.context;
}

// ─── Types ───────────────────────────────────────────────────────────────────

type Snapshot = SnapshotFrom<typeof joinOrSignInStateMachine>;

type UseJoinOrSignInParams = {
  onFrameHeightChange: (height: number) => void;
};
